import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  BarChart3,
  BriefcaseBusiness,
  CircleUserRound,
  Euro,
  ListChecks,
  LogOut,
  MapPin,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { partnerLogout } from "@/integrations/php/client";
import { PartnerProfile } from "@/types/partnerPortal";

const partnerNavigation = (partnerId?: string) => [
  { label: "Εργασίες", path: `/partner/${partnerId}/pipeline`, icon: BriefcaseBusiness, description: "Τι χρειάζεται προσοχή" },
  { label: "Όλα τα leads", path: `/partner/${partnerId}/leads`, icon: ListChecks, description: "Αναζήτηση και φίλτρα" },
  { label: "Απόδοση", path: `/partner/${partnerId}/analytics`, icon: BarChart3, description: "Στατιστικά και τάσεις" },
  { label: "Δραστηριότητα", path: `/partner/${partnerId}/activity`, icon: Activity, description: "Ιστορικό ενεργειών" },
  { label: "Χρεώσεις", path: `/partner/${partnerId}/billing`, icon: Euro, description: "Ανάλυση ανά lead" },
  { label: "Ρυθμίσεις", path: `/partner/${partnerId}/settings`, icon: Settings, description: "Προφίλ και ειδοποιήσεις" },
];

interface PartnerSidebarProps {
  partner: PartnerProfile;
}

const PartnerSidebar: React.FC<PartnerSidebarProps> = ({ partner }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();
  const navItems = partnerNavigation(partnerId);

  return (
    <aside className="sticky top-0 hidden h-[100dvh] w-[276px] shrink-0 flex-col border-r border-slate-800 bg-slate-950 text-white md:flex">
      <div className="border-b border-slate-800 px-5 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500 text-white">
            <BriefcaseBusiness className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-bold tracking-tight">Diathermiki</p>
            <p className="text-xs text-slate-400">Portal συνεργατών</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5" aria-label="Κύρια πλοήγηση">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <button
              key={item.path}
              type="button"
              onClick={() => navigate(item.path)}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400",
                active ? "bg-sky-500 text-white" : "text-slate-300 hover:bg-slate-900 hover:text-white",
              )}
              aria-current={active ? "page" : undefined}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{item.label}</span>
                <span className={cn("block truncate text-[11px]", active ? "text-sky-100" : "text-slate-500")}>{item.description}</span>
              </span>
            </button>
          );
        })}
      </nav>

      <div className="border-t border-slate-800 p-4">
        <div className="mb-3 rounded-xl border border-slate-800 bg-slate-900 p-3">
          <div className="flex items-start gap-2.5">
            <CircleUserRound className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{partner.name}</p>
              <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-400">
                <MapPin className="h-3 w-3" /> {partner.location || "Χωρίς τοποθεσία"}
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void partnerLogout()}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold text-slate-400 transition-colors hover:bg-slate-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
        >
          <LogOut className="h-4 w-4" /> Αποσύνδεση
        </button>
      </div>
    </aside>
  );
};

export default PartnerSidebar;
